import { createContext, useState, useEffect } from 'react';
import ReturnViewModel from '../Contexts/ReturnViewModel';
import LoginViewModel from './ViewModels/LoginViewModel';
import ForgotPasswordViewModel from './ViewModels/ForgotPasswordViewModel';
import ResetPasswordViewModel from './ViewModels/ResetPasswordViewModel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import { trackException } from '../../applicationInsights';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { buildTelemetryHeaders } from '../Contexts/DeviceTelemetry';

type ContextModel = {
    authenticated: boolean;
    userDetails: UserDetailsViewModel | null;
    login: (payload: LoginViewModel, callback: (result: ReturnViewModel) => void) => void;
    forgotPassword: (payload: ForgotPasswordViewModel, callback: (result: ReturnViewModel) => void) => void;
    resetPassword: (payload: ResetPasswordViewModel, callback: (result: ReturnViewModel) => void) => void;
    logout: () => void;
    refreshCredentials: (callback: () => void) => void;
    refreshUserDetails: () => Promise<void>;
    checkRole: (requiredRole: string[]) => boolean;
    checkLicence: (requiredLicence: string[]) => boolean;
};

export const AuthContext = createContext<ContextModel>({
    authenticated: false,
    userDetails: null,
    login: () => { throw Error('Cannot use a context without a provider'); },
    forgotPassword: () => { throw Error('Cannot use a context without a provider'); },
    resetPassword: () => { throw Error('Cannot use a context without a provider'); },
    logout: () => { throw Error('Cannot use a context without a provider'); },
    checkRole: () => { throw Error('Cannot use a context without a provider'); },
    checkLicence: () => { throw Error('Cannot use a context without a provider'); },
    refreshCredentials: () => { throw Error('Cannot use a context without a provider'); },
    refreshUserDetails: () => { throw Error('Cannot use a context without a provider'); }
});

export default function AuthContextProvider({ children }: React.PropsWithChildren<{}>) {
    const navigate = useNavigate();
    const [authenticated, setAuthenticated] = useState(false);
    const [ready, setReady] = useState(false);
    const [openBanner, setOpenBanner] = useState(false);
    const [userDetails, setUserDetails] = useState<UserDetailsViewModel | null>(null);

    useEffect(() => {
        getUserDetails();
        const consent = localStorage.getItem('cookieConsent');
        if (!consent) {
            setOpenBanner(true);
        }
    }, []);

    useEffect(() => {
        if (!authenticated) return;

        const HEARTBEAT_MS = 15 * 60 * 1000;

        const sendHeartbeat = async () => {
            try {
                await Promise.allSettled([
                    fetch('api/TelemetrySession/heartbeat', {
                        method: 'POST',
                        credentials: 'include',
                        headers: buildTelemetryHeaders()
                    }),
                    fetch('api/Telemetry/heartbeat', {
                        method: 'POST',
                        credentials: 'include',
                        headers: buildTelemetryHeaders()
                    })
                ]);
            } catch {
                // monitoring-only: ignore
            }
        };

        // fire once shortly after auth becomes true
        sendHeartbeat();

        const id = window.setInterval(sendHeartbeat, HEARTBEAT_MS);
        return () => window.clearInterval(id);
    }, [authenticated]);

    const getUserDetails = async () => {
        const apiPath = `auth/getuserdetails`;
        try {
            const response = await fetch(`api/${apiPath}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include'
            });

            const result: ReturnViewModel = await response.json();
            if (result.success) {
                setUserDetails(result.value);
                setAuthenticated(true);
            } else {
                setAuthenticated(false);
                result.messages.forEach(message => console.error(`${apiPath}: ${message}`));
            }
        } catch (error: any) {
            console.error(`${apiPath}: ${error}`);
            trackException(error);
        } finally {
            setReady(true);
        }
    }

    const login = async (payload: LoginViewModel, callback: (data: ReturnViewModel) => void) => {
        const apiPath = `auth/login`;
        setReady(false);

        try {
            const response = await fetch(`api/${apiPath}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'include'
            });

            const result: ReturnViewModel = await response.json();
            if (result.success) {
                await getUserDetails();
            }
            else {
                setReady(true);
            }
            callback?.(result);
        } catch (error: any) {
            console.error(`${apiPath}: ${error}`);
            trackException(error);
        }
    }

    const logout = async () => {
        const apiPath = `auth/logout`;
        setReady(false);

        try {
            const response = await fetch(`api/${apiPath}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include'
            });

            const result: ReturnViewModel = await response.json();
            if (result.success) {
                setAuthenticated(false);
                setUserDetails(null);
                window.location.href = 'https://trics.org/';
            } else {
                result.messages.forEach(message => console.error(`${apiPath}: ${message}`));
            }
        } catch (error: any) {
            console.error(`${apiPath}: ${error}`);
            trackException(error);
        } finally {
            setReady(true);
        }
    }

    const forgotPassword = async (payload: ForgotPasswordViewModel, callback: (data: ReturnViewModel) => void) => {
        const apiPath = `auth/forgotpassword`;
        setReady(false);
        try {
            const response = await fetch(`api/${apiPath}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'include'
            });

            const result: ReturnViewModel = await response.json();
            if (!result.success) {
                result.messages.forEach(message => console.error(`${apiPath}: ${message}`));
            }
            callback(result);
        } catch (error: any) {
            console.error(`${apiPath}: ${error}`);
            trackException(error);
        } finally {
            setReady(true);
        }
    }

    const resetPassword = async (payload: ResetPasswordViewModel, callback: (data: ReturnViewModel) => void) => {
        const apiPath = `auth/resetpassword`;
        setReady(false);
        try {
            const response = await fetch(`api/${apiPath}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'include'
            });

            const result: ReturnViewModel = await response.json();
            if (!result.success) {
                result.messages.forEach(message => console.error(`${apiPath}: ${message}`));
            }
            callback(result);
        } catch (error: any) {
            console.error(`${apiPath}: ${error}`);
            trackException(error);
        } finally {
            setReady(true);
        }
    }

    const checkRole = (requiredRole: string[]) => {
        if (!userDetails) return false;

        const { role } = userDetails;
        if (role === 'SuperAdmin') return true;

        return requiredRole.includes(userDetails.role);
    }

    const checkLicence = (requiredLicence: string[]): boolean => {
        if (!userDetails) return false;

        let { licence } = userDetails;
        if (licence === 'SUPER_ADMIN') return true;
        if (licence === 'Administrator') licence = 'Multi-Office';

        return requiredLicence.includes(licence);
    };

    const refreshCredentials = async (callback: () => void) => {
        const apiPath = `auth/refreshtoken`;
        setReady(false);
        try {
            const response = await fetch(`api/${apiPath}`, {
                method: 'GET',
                credentials: 'include'
            });

            const result: ReturnViewModel = await response.json();
            if (result.success) {
                callback();
            }
            else {
                setAuthenticated(false);
                setUserDetails(null);
                navigate(`/authorisation/login?return=${encodeURI(window.location.href)}`);
                result.messages.forEach(message => console.error(`${apiPath}: ${message}`));
            }
        } catch (error: any) {
            console.error(`${apiPath}: ${error}`);
            trackException(error);
        } finally {
            setReady(true);
        }
    }

    const handleAccept = () => {
        localStorage.setItem('cookieConsent', 'true');
        setOpenBanner(false);
    };

    const context: ContextModel = {
        authenticated,
        userDetails,
        login,
        forgotPassword,
        resetPassword,
        logout,
        checkRole,
        checkLicence,
        refreshCredentials,
        refreshUserDetails: getUserDetails
    }

    return (
        <AuthContext.Provider value={context}>
            {children}
            <Snackbar
                open={openBanner}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity="info"
                    action={
                        <Button color="primary" size="small" onClick={handleAccept}>
                            Got it
                        </Button>
                    }
                    sx={{ width: '100%' }}
                >
                    We use essential cookies to keep you logged in and improve security.
                </Alert>
            </Snackbar>
            <Backdrop
                open={!ready}
                sx={{
                    backgroundColor: '#ffffff',
                    zIndex: (theme) => theme.zIndex.modal + 1,
                }}
            >
                <CircularProgress size="100px" color="primary" />
            </Backdrop>
        </AuthContext.Provider >
    );
}

export class UserDetailsViewModel {
    public userId: string = '';
    public userEmail: string = '';
    public userFullName: string = '';
    public role: string = '';
    public organisationId: string = '';
    public organisationName: string = '';
    public memberNumber: string = '';
    public siteId: string = '';
    public siteName: string = '';
    public licence: string = '';
    public offline: boolean = false;
    public mailchimpOptedIn: boolean = false;
}

